/**
 * Tenant Configuration
 * Maps tenant IDs to their respective frontend roots
 */

import type { AuthenticatedUser } from "../api/auth";
import { TenantRoot as Tenant68e43b18e0652b6ee86a59d6 } from "./68e43b18e0652b6ee86a59d6";
import { TenantRoot as Tenant69031406f09fed7ea2d97585 } from "./69031406f09fed7ea2d97585";
import { TenantRoot as Tenant693a810a08eca29d54af2734 } from "./693a810a08eca29d54af2734";
import { TenantRoot as Tenant6911e4b63ac6f36785d954a7 } from "./6911e4b63ac6f36785d954a7";
import { FallbackTenant } from "./FallbackTenant";
import { AnonymousTenant } from "./AnonymousTenant";

export interface TenantConfig {
  id: string;
  name: string;
  component: React.ComponentType<{ currentUser?: AuthenticatedUser }>;
  landingPath: string; // Default path after login
}

/**
 * Default anonymous tenant ID
 * Used when no user is logged in
 */
export const DEFAULT_ANONYMOUS_TENANT_ID = "anonymous";

/**
 * Registry of all available tenants
 * Maps tenant ID to configuration
 */
export const TENANT_REGISTRY: Record<string, TenantConfig> = {
  // IntecIO Tenant
  "693a810a08eca29d54af2734": {
    id: "693a810a08eca29d54af2734",
    name: "IntecIO",
    component: Tenant693a810a08eca29d54af2734,
    landingPath: "/"
  },
  // IntecAI Tenant
  "6911e4b63ac6f36785d954a7": {
    id: "6911e4b63ac6f36785d954a7",
    name: "Intec.AI",
    component: Tenant6911e4b63ac6f36785d954a7,
    landingPath: "/"
  },
  // Kirchhoff Tenant (Neuer Tenant)
  "69031406f09fed7ea2d97585": {
    id: "69031406f09fed7ea2d97585",
    name: "Kirchhoff",
    component: Tenant69031406f09fed7ea2d97585,
    landingPath: "/"
  },
  // Legacy Tenant (wird zu Demos umbenannt)
  "68e43b18e0652b6ee86a59d6": {
    id: "68e43b18e0652b6ee86a59d6",
    name: "Pfleiderer",
    component: Tenant68e43b18e0652b6ee86a59d6,
    landingPath: "/"
  },
  // Anonymous Default Tenant (for non-authenticated users)
  [DEFAULT_ANONYMOUS_TENANT_ID]: {
    id: DEFAULT_ANONYMOUS_TENANT_ID,
    name: "IntecIO Cloud",
    component: AnonymousTenant,
    landingPath: "/"
  }
  // Add more tenants here as needed
};

/**
 * Get tenant config by ID
 * Returns fallback tenant if no specific configuration exists
 */
export function getTenantConfig(tenantId: string): TenantConfig {
  // Check if tenant has specific configuration
  if (TENANT_REGISTRY[tenantId]) {
    return TENANT_REGISTRY[tenantId];
  }

  // Return fallback configuration for tenants without UI
  return {
    id: tenantId,
    name: "Neuer Mandant",
    component: FallbackTenant,
    landingPath: "/"
  };
}

/**
 * Get or set tenant from storage
 */
const STORAGE_KEY = "intecio-tenant-id";

export function storeTenantSlug(tenantId: string): void {
  sessionStorage.setItem(STORAGE_KEY, tenantId);
}

export function retrieveTenantSlug(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function clearTenantSlug(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
